import type {
  DashboardSummary,
  IncidentDetail,
  IncidentListItem,
  IncidentNotification,
  IncidentStatus,
  IncidentReport,
  ReportFilters,
  TenantRole,
  TenantSummary,
} from "@scim/shared";

export interface MeResponse {
  user: { id: string; displayName: string; discordUserId: string | null };
  tenants: TenantSummary[];
}

export interface SettingsResponse {
  tenant: {
    id: string;
    name: string;
    timezone: string;
    ticketProvider: "internal" | "github" | "jira";
    ticketProject: string | null;
    reportChannelId: string | null;
    weeklyReportsEnabled: boolean;
    monthlyReportsEnabled: boolean;
    slaHours: Record<"P1" | "P2" | "P3" | "P4", number>;
  };
  github: {
    configured: boolean;
    owner: string;
    repository: string;
    projectOwner: string | null;
    projectNumber: number | null;
    labels: string[];
    assignees: string[];
  } | null;
  guilds: Array<{
    guildId: string;
    guildName: string;
    incidentChannelId: string | null;
    incidentChannelName: string | null;
    allowedRoleIds: string[];
    p1RoleIds: string[];
  }>;
}

export interface TenantMember {
  id: string;
  discordUserId: string | null;
  displayName: string;
  avatarUrl: string | null;
  role: TenantRole;
  createdAt: string;
  hasLoggedIn: boolean;
}

export interface MembersResponse {
  members: TenantMember[];
  canManage: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("content-type", "application/json");
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(payload?.error ?? `Request failed: ${response.status}`, response.status);
  }
  return response.json() as Promise<T>;
}

export const api = {
  me: () => request<MeResponse>("/api/me"),
  dashboard: (tenantId: string) => request<DashboardSummary>(`/api/tenants/${tenantId}/dashboard`),
  incidents: (tenantId: string, filters: { status: string; severity: string; query: string }) => {
    const params = new URLSearchParams(filters);
    return request<{ incidents: IncidentListItem[] }>(
      `/api/tenants/${tenantId}/incidents?${params}`,
    );
  },
  incident: (tenantId: string, incidentId: string) =>
    request<{ incident: IncidentDetail }>(`/api/tenants/${tenantId}/incidents/${incidentId}`),
  updateIncident: (
    tenantId: string,
    incidentId: string,
    input: {
      status?: IncidentStatus;
      assigneeName?: string | null;
      rootCause?: string | null;
      recurrenceKey?: string | null;
      postmortemStatus?: "not_required" | "pending" | "draft" | "published";
      postmortemUrl?: string | null;
    },
  ) =>
    request<{ incident: IncidentDetail }>(`/api/tenants/${tenantId}/incidents/${incidentId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  settings: (tenantId: string) => request<SettingsResponse>(`/api/tenants/${tenantId}/settings`),
  updateSettings: (tenantId: string, input: Record<string, unknown>) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/settings`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  members: (tenantId: string) => request<MembersResponse>(`/api/tenants/${tenantId}/members`),
  inviteMember: (
    tenantId: string,
    input: { discordUserId: string; displayName?: string; role: TenantRole },
  ) =>
    request<{ ok: true; userId: string }>(`/api/tenants/${tenantId}/members`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateMemberRole: (tenantId: string, userId: string, role: TenantRole) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/members/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),
  removeMember: (tenantId: string, userId: string) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/members/${userId}`, {
      method: "DELETE",
    }),
  report: (tenantId: string, filters: ReportFilters) => {
    const params = new URLSearchParams(
      Object.entries(filters).map(([key, value]) => [key, String(value)]),
    );
    return request<{ report: IncidentReport }>(`/api/tenants/${tenantId}/reports?${params}`);
  },
  reportCsvUrl: (tenantId: string, filters: ReportFilters) => {
    const params = new URLSearchParams(
      Object.entries(filters).map(([key, value]) => [key, String(value)]),
    );
    return `/api/tenants/${tenantId}/reports.csv?${params}`;
  },
  shareReport: (tenantId: string, filters: ReportFilters) =>
    request<{ url: string; expiresAt: string }>(`/api/tenants/${tenantId}/reports/share`, {
      method: "POST",
      body: JSON.stringify({ filters }),
    }),
  deliverReport: (tenantId: string, filters: ReportFilters) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/reports/deliver`, {
      method: "POST",
      body: JSON.stringify({ filters }),
    }),
  reportSnapshots: (tenantId: string) =>
    request<{
      snapshots: Array<{
        id: string;
        reportType: "weekly" | "monthly" | "manual";
        periodKey: string;
        fromAt: string;
        toAt: string;
        createdAt: string;
      }>;
    }>(`/api/tenants/${tenantId}/reports/snapshots`),
  notifications: (tenantId: string) =>
    request<{ notifications: IncidentNotification[]; unreadCount: number }>(
      `/api/tenants/${tenantId}/notifications`,
    ),
  markNotificationsRead: (tenantId: string, keys: string[]) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/notifications/read`, {
      method: "POST",
      body: JSON.stringify({ keys }),
    }),
};
